import React, { useState, useEffect } from "react";
import SpinningLoader from "@src/components/common/SpinningLoader";
import ProductListRow from "@src/components/widgets/ProductListRow";
import * as ProductApi from "@src/api/ProductApi";
import { useToast } from "@swordsweeper/ssi-ui-component-library";
import SectionHeader from "../SectionHeader";
import ProductPricingTable from "../ProductPricingTable";
import ProductImage from "../ProductImage";

interface ProductOtherOptionsProps {
  productId: string;
  productImageSrc?: string;
}

export default function ProductOtherOptions({
  productId,
  productImageSrc,
}: ProductOtherOptionsProps) {
  const { showToast } = useToast();
  const [isLoadingProductPricing, setIsLoadingProductPricing] = useState(true);
  const [isLoadingSimilarProducts, setIsLoadingSimilarProducts] =
    useState(true);

  const [productPricing, setProductPricing] = useState<any>(null);
  const [similarProducts, setSimilarProducts] = useState<any[]>([]);

  useEffect(() => {
    if (productId) {
      handleLoadProductPricing();
      handleLoadSimilarProducts();
    }
  }, [productId]);

  const handleLoadProductPricing = async (): Promise<void> => {
    setIsLoadingProductPricing(true);
    try {
      const response = await ProductApi.getProductPricing(productId);
      setProductPricing(response.body);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading product pricing",
        variant: "error",
      });
    } finally {
      setIsLoadingProductPricing(false);
    }
  };

  const handleLoadSimilarProducts = async (): Promise<void> => {
    setIsLoadingSimilarProducts(true);
    try {
      const response = await ProductApi.getSimilarProducts(productId, {
        pageSize: 4,
      });
      setSimilarProducts(response.body.results);
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading similar products",
        variant: "error",
      });
    } finally {
      setIsLoadingSimilarProducts(false);
    }
  };

  return (
    <div className="flex-column spaced-content-v-lg">
      {isLoadingProductPricing || isLoadingSimilarProducts ? (
        <div className="min-height-400">
          <SpinningLoader />
        </div>
      ) : (
        <div>
          <div className="flex-row spaced-content-xl">
            <ProductImage imageSrc={productImageSrc} sizePx={180} />
            <ProductPricingTable simplified data={productPricing || []} />
          </div>

          {similarProducts.length > 0 && (
            <div className="padding-top-xxl">
              <SectionHeader header="Similar Products" />
              <ProductListRow
                products={similarProducts}
                tileClassName="height-initial-important"
                imageSizePx={200}
                idContext="product-other-options-product-tile"
                data-testid="product-other-options-product-list-row"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
